import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy, ContentMaps, CreateBibleStudyDto } from '../../../api/models/BibleStudy';
import { CreateBibleStudySection } from '../../../api/models/BibleStudySection';
import { UploadResult } from '../../../api/models/UploadResult';
import { Environment } from '../../../Environment';
import { routes } from '../../../route-list';
import { getModuleRequestBodyLibrary, moduleUploadLib } from '../../../util/bible-study-module';
import { convertHoursToMinutes } from '../../../util/convert-time';
import { useRequest } from '../../../util/use-request';
import { isDuplicated, validateFileSize, validateModuleFormState } from '../../../util/validator';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { BibleStudyFormState, ModuleFormState } from '../bible-study-form';
import { generateFileInputKeys, getDayOptions, getModuleArrayIndex } from '../bible-study-form.helper';
import { BibleStudyFormScreen } from '../components/BibleStudyFormScreen';

export type BibleStudyFormProps = {
  // Outputs
  mode: 'add' | 'edit';
  /** if user wants to edit title and desc or not */
  isEditingTitle: boolean;
  /** the current form state */
  form: BibleStudyFormState;
  /** if request for saving is currently ongoing */
  isSaving: boolean;
  /** sections of the current program */
  sections: CreateBibleStudySection[];
  /** if modal to add new module is open or not */
  isModuleModalOpen: boolean;
  /** form for the section module */
  moduleForm: ModuleFormState;
  /** some form of error for the module modal */
  moduleFormError: string | null;
  /** if file upload on module form modal is currently taking place */
  isModuleFormFileUploading: boolean;
  /**
   * keys for input[type=file] for resetting value
   * see: https://github.com/redux-form/redux-form/issues/769
   */
  fileInputKeys: { image: string; video: string; scripture: string; audio: string };
  /** if fetching existing module details for edit module */
  isModuleFormLoading?: boolean;
  isLoadingIntroductionVideo?: boolean;
  /** Example: {label: 'Week 1', value: 'Week 1'} */
  weekTagOptions: { label: string; value: string }[];
  /** Example: {label: 'Day 1', value: 'Day 1'} */
  dayTagOptions: { label: string; value: string }[];
  isContentFormModalOpen: boolean;
  isGuidedModalOpen: boolean;

  // Inputs
  /** called when the user wants to edit title and description */
  userClickedEditTitle: () => void;
  /** called when the user edits the fields */
  userUpdatedForm: React.Dispatch<React.SetStateAction<BibleStudyFormState>>;
  /** called when user wants to save bible study */
  userClickedSave: () => void;
  /** user wants to add a tag to tags */
  userPushedToTags: () => void;
  /** open or close module modal */
  userToggledModuleModal: () => void;
  /** called when the user edits the module fields */
  userUpdatedModuleForm: (formState: ModuleFormState) => void;
  /** user wants to reset the module form */
  userResetModuleForm: () => void;
  /** user submits the module form */
  userSubmittedModuleForm: () => void;
  /** user wants to cancel creation */
  userClickedCancel: () => void;
  /** user wants to update sections array */
  userUpdatedSections: (sections: CreateBibleStudySection[]) => void;
  /** user chooses a module file (video or image) */
  userChoseModuleFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** user wants to delete module file on form */
  userClickedDeleteModuleFormFile: () => void;
  /** user wants to edit an existing module (for edit mode) */
  userClickedEditModule?: (weekTag: string, dayTag: string, moduleDayIndex: number) => void;
  /** user wants to delete an existing module (for edit mode) */
  userClickedDeleteModule?: (weekTag: string, dayTag: string, moduleDayIndex: number) => void;
  /** user wants to delete a whole section (for edit mode) */
  userClickedDeleteSection?: (sectionIndex: number) => void;
  /** handler for when the user clicks "delete" from the "more" dropdown */
  userClickedDeleteStudy?: () => void;
  /** called when user clicks "save" on "add content +" button */
  userClickedSaveViaContentModal: () => void;
  userClickedAddContentViaModal: () => void;
  userClickedSubmitonGuidedModal: (value: ContentMaps) => void;
  userClickedCancelonGuidedModal: () => void;
  userClickedEditContentMap: () => void;
  userClickedResetForm?: () => void;
};

export const initialStudyFormState: BibleStudyFormState = {
  title: '',
  description: '',
  tag: '',
  tags: [],
  publishingStatus: 'unpublished',
  hours: 0,
  minutes: 0,
  contentMaps: [],
  categories: [],
};

export const initialModuleFormState: ModuleFormState = {
  sectionIndex: null,
  type: null,
  title: '',
  scriptures: [{ id: '1', scriptureIds: [] }],
  questionAndAnswers: [{ question: '', answer: '' }],
  translationId: null,
  weekTag: '',
  dayTag: '',
};

/* -------------------------------------------------------------------------- */
/* START::MainContainer                                                       */
/* -------------------------------------------------------------------------- */

export const BibleStudyFormContainer = () => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<BibleStudyFormState>(initialStudyFormState);
  const [sections, setSections] = useState<CreateBibleStudySection[]>([]);
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [moduleForm, setModuleForm] = useState<ModuleFormState>(initialModuleFormState);
  // state for file-related errors
  const [moduleFormError, setModuleFormError] = useState<string | null>(null);
  const [isModuleFormFileUploading, setIsModuleFormFileUploading] = useState(false);
  // we need keys to clear out input[file] value
  const [fileInputKeys, setFileInputKeys] = useState(generateFileInputKeys());
  const [isContentFormModalOpen, setIsContentFormModalOpen] = useState(false);
  const [isGuidedModalOpen, setIsGuidedModalOpen] = useState(false);

  const history = useHistory();
  const request = useRequest();

  const { services } = Environment.current();

  // Load auto-saved file on mount
  useEffect(() => {
    const saved = services.formAutoSaver.get();
    if (saved && saved.sections.length) {
      setForm(saved.study);
      setSections(saved.sections);
      toast.info('Previous session restored!');
    }
  }, []);

  // Trigger auto-save when sections change
  useEffect(() => {
    const { featureImage, introVideo, ...study } = form;
    services.formAutoSaver.save({ study, sections });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  return (
    <BibleStudyFormScreen
      mode="add"
      form={form}
      moduleForm={moduleForm}
      isSaving={isSaving}
      isEditingTitle={isEditingTitle}
      isModuleModalOpen={isModuleModalOpen}
      isContentFormModalOpen={isContentFormModalOpen}
      sections={sections}
      moduleFormError={moduleFormError}
      isModuleFormFileUploading={isModuleFormFileUploading}
      fileInputKeys={fileInputKeys}
      weekTagOptions={form.contentMaps.map(item => ({ label: `Week ${item.week}`, value: `Week ${item.week}` }))}
      dayTagOptions={getDayOptions(form.contentMaps, moduleForm.weekTag)}
      isGuidedModalOpen={isGuidedModalOpen}
      userClickedResetForm={() => {
        setForm(initialStudyFormState);
        setSections([]);
      }}
      userClickedCancelonGuidedModal={() => {
        setIsGuidedModalOpen(false);
      }}
      userClickedSubmitonGuidedModal={contentMaps => {
        setForm(prevState => ({ ...prevState, contentMaps: contentMaps }));
        setIsGuidedModalOpen(false);
      }}
      userClickedEditContentMap={() => setIsGuidedModalOpen(true)}
      userClickedAddContentViaModal={() => {
        // If there is no content map, show the guided content modal
        if (!form.contentMaps.length) {
          setIsGuidedModalOpen(true);
        } else {
          // Else, open the content form modal
          setIsContentFormModalOpen(prev => !prev);
        }
      }}
      userUpdatedForm={setForm}
      userUpdatedModuleForm={setModuleForm}
      userClickedSaveViaContentModal={() => {
        // When I add a new section tag...

        // Perform form validation
        const formError = validateModuleFormState(moduleForm);
        if (formError || moduleFormError) {
          toast.error(formError || 'Module form has some errors');
          return;
        }

        // Generate module from form
        const newModule = getModuleRequestBodyLibrary(moduleForm)[moduleForm.type!];

        // Check if section tag already exists
        const sectionIndex = sections.findIndex(
          section => section.name.toLowerCase() === moduleForm.weekTag.toLowerCase()
        );
        const sectionExists = sectionIndex !== -1;

        if (sectionExists) {
          // If section exists, push content to that section
          const newSections = [...sections];
          const selectedSection = sections[sectionIndex];
          newSections.splice(sectionIndex, 1, {
            ...selectedSection,
            modules: [...selectedSection.modules, newModule],
          });
          setSections(newSections);
        } else {
          // If it doesnt exist yet, create new section and add tag to [weekTags]
          setSections(prev => [
            ...prev,
            {
              name: moduleForm.weekTag,
              modules: [newModule],
              tag: moduleForm.weekTag,
            },
          ]);
        }

        // Reset form
        setModuleForm(initialModuleFormState);
        // Close form modal
        setIsContentFormModalOpen(false);
      }}
      userClickedEditTitle={() => {
        setIsEditingTitle(!isEditingTitle);
      }}
      userClickedSave={async () => {
        // additional validation, if user enables button on inspect elements
        if (!form.title || !form.description) {
          toast.error('Study must have a title and description before saving.');
          return;
        }

        if (form.contentMaps == null || form.contentMaps.length == 0) {
          toast.error('Study must set content map');
          setIsGuidedModalOpen(true);
          return;
        }
        try {
          setIsSaving(true);

          const payload: CreateBibleStudyDto = {
            description: form.description.trim(),
            title: form.title.trim(),
            tags: form.tags,
            taxonomyCategory: 'string',
            publishingStatus: form.publishingStatus || 'published',
            duration: convertHoursToMinutes(form.hours, form.minutes),
            contentMaps: form.contentMaps,
            categories: form.categories,
            imageUrl: '',
          };

          if (form.featureImage) {
            const uploadResult = await request<UploadResult>(apiConfigs.uploadImage(form.featureImage));
            payload.imageUrl = uploadResult.filePath;
          }
          if (form.introVideo) {
            const uploadResult = await request<UploadResult>(apiConfigs.uploadVideo(form.introVideo));
            payload.introMuxId = uploadResult.muxID;
          }

          // create initial bible study
          const bibleStudyResult = await request<BibleStudy>(apiConfigs.addBibleStudy(payload));

          // update bible study sections
          await request(
            apiConfigs.putBibleStudy({
              ...bibleStudyResult,
              // Populate tag with name if it's empty
              sections: sections.map(section => (!section.tag ? { ...section, tag: section.name } : section)),
            })
          );

          toast.success('Study created!');

          services.formAutoSaver.clear();

          history.push(routes.BIBLE_STUDIES);
        } catch (e: any) {
          toast.error(e.message || 'Failed to create study');
          setIsSaving(false);
        }
      }}
      userPushedToTags={() => {
        // validate tags for duplicates
        if (isDuplicated(form.tags, form.tag)) {
          toast.error(`Duplicate tag "${form.tag}"`);
          return;
        }
        const newTags = [...form.tags, form.tag];
        setForm({ ...form, tags: newTags, tag: '' });
      }}
      userToggledModuleModal={() => setIsModuleModalOpen(!isModuleModalOpen)}
      userResetModuleForm={() => {
        setModuleForm(initialModuleFormState);
        setModuleFormError(null);
        setFileInputKeys(generateFileInputKeys());
      }}
      userClickedDeleteSection={sectionIndex => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this section?"
              onClose={options.onClose}
              onOk={() => {
                // remove a section
                const newSections: CreateBibleStudySection[] = Array.from(sections)
                  .filter((_, i) => i !== sectionIndex)
                  .map((section, i) => ({ ...section }));
                setSections(newSections);
                options.onClose();
              }}
            />
          ),
        });
      }}
      userSubmittedModuleForm={() => {
        // Form validation
        const formError = validateModuleFormState(moduleForm);
        if (formError || moduleFormError) {
          toast.error(formError || 'Module form has some errors');
          return;
        }

        const newSections: CreateBibleStudySection[] = sections.map((section, i) => {
          // The [getModuleRequestBodyLibrary] function
          // abstracts the generation of module format for each type
          const newModule = getModuleRequestBodyLibrary(moduleForm)[moduleForm.type!];
          return moduleForm.sectionIndex === i ? { ...section, modules: [...section.modules, newModule] } : section;
        });

        setSections(newSections);
        setModuleForm(initialModuleFormState);
        setIsModuleModalOpen(false);
      }}
      userClickedCancel={() => {
        // Check first if the form is pristine. If pristine, proceed without confirmation.
        if (_.isEqual(form, initialStudyFormState)) {
          history.push(routes.BIBLE_STUDIES);
        } else {
          confirmAlert({
            customUI: options => (
              <RhConfirmModal
                description="Are you sure you want to exit without saving?"
                isOpen={true}
                onClose={options.onClose}
                onOk={() => {
                  services.formAutoSaver.clear();
                  options.onClose();
                  history.push(routes.BIBLE_STUDIES);
                }}
              />
            ),
          });
        }
      }}
      userUpdatedSections={setSections}
      userChoseModuleFile={async e => {
        setModuleFormError(null);

        if (!e.target.files?.length) return;
        const file = e.target.files[0];

        const fileSizeError = validateFileSize(file);
        if (fileSizeError) {
          toast.error(fileSizeError);
          return;
        }

        setModuleForm(prev => ({ ...prev, data: file, fileName: file.name }));

        try {
          setIsModuleFormFileUploading(true);
          const src = await moduleUploadLib[moduleForm.type!](request, file);
          setModuleForm(prev => ({ ...prev, url: src }));
        } catch (e: any) {
          toast.error(e.message || 'Failed to upload file');
          setModuleFormError(`Failed to upload ${file.name}`);
        } finally {
          setIsModuleFormFileUploading(false);
        }
      }}
      userClickedDeleteModuleFormFile={() => {
        setModuleForm(prev => ({ ...prev, data: null, url: null, fileName: null }));
        setModuleFormError(null);
        setFileInputKeys(generateFileInputKeys());
      }}
      userClickedDeleteModule={(weekTag, dayTag, moduleDayIndex) => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this module?"
              onClose={options.onClose}
              onOk={() => {
                const moduleIndex = getModuleArrayIndex(sections, { weekTag, dayTag, moduleDayIndex });
                const sectionIndex = sections.findIndex(
                  section => (section.tag || section.name).toLowerCase() === weekTag.toLowerCase()
                );

                // Remove module from section
                const newSections: CreateBibleStudySection[] = sections.map((section, i) => {
                  const modules = Array.from(section.modules).filter((_, i) => i !== moduleIndex);
                  return sectionIndex === i ? { ...section, modules } : section;
                });

                setSections(newSections);
                options.onClose();
              }}
            />
          ),
        });
      }}
    />
  );
};
